import { Button, Select } from '@chakra-ui/react';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import ProductsTable from '../../../components/Products/ProductsTable.js';
import { columnsDataProducts } from './variables/columnsDataProducts.js';
import React, { useEffect, useState } from 'react';
import { getData, postData, putData } from '../../../utils/httpClient.js';
import { useSearch } from '../../../contexts/SearchContext.js';
import ProductsEditModal from '../../../components/Products/ProductsEditModal';
import { useResetPageOnFilterChange } from '../../../hooks/useResetPageOnFilterChange.js';
import { useTableSorting } from '../../../hooks/useTableSorting.js';
import { useExtraTableFilter } from '../../../hooks/useExtraTableFilter.js';
import authService from '../../../services/authService.js';

export default function Settings() {
  const canEdit =
    !authService.isMechanic() &&
    authService.constructor.checkPermissionFromStorage('products.edit');
  const { searchQuery, resetListFilters } = useSearch();
  const {
    draft: modelFilter,
    setDraft: setModelFilter,
    applied: appliedModelFilter,
  } = useExtraTableFilter('');
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [modalState, setModalState] = useState({
    isOpen: false,
    product: null,
    isNew: false,
  });
  const [models, setModels] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { sorting, setSorting, sortBy, sortDir, appendSortParams } = useTableSorting();

  useResetPageOnFilterChange(setPage, page, [searchQuery, appliedModelFilter, rowsPerPage, sortBy, sortDir]);

  const loadModels = async () => {
    try {
      const response = await getData('/models', { skipLoader: true });
      const list = response?.payload?.models || [];
      setModels(
        [...list].sort((a, b) =>
          String(a.description || '').localeCompare(
            String(b.description || ''),
            'es',
            { numeric: true },
          ),
        ),
      );
    } catch {
      setModels([]);
    }
  };

  const loadData = async (dato, modelId) => {
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        data: dato || '',
      });
      if (modelId) params.set('modelId', String(modelId));
      appendSortParams(params);
      const response = await getData(`/products?${params.toString()}`);
      setData(response?.payload?.products || []);
      setTotalPages(Math.ceil((response?.payload?.total || 0) / rowsPerPage));
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  useEffect(() => {
    loadModels();
  }, []);

  useEffect(() => {
    loadData(searchQuery, appliedModelFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery, appliedModelFilter, rowsPerPage, sortBy, sortDir]);

  useEffect(() => {
    resetListFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNewProductModal = () => {
    setModalState({
      isOpen: true,
      product: {
        id: null,
        name: '',
        category: '',
        currentCost: '',
        currentStock: '',
        unit: '',
      },
      isNew: true,
    });
  };

  const closeEditModal = () => {
    setModalState({ isOpen: false, product: null, isNew: false });
  };

  const handleCreateProduct = async (newProduct) => {
    await postData('/products', newProduct);
    await loadData(searchQuery, appliedModelFilter);
  };

  const handleUpdateProduct = async (updatedProduct) => {
    try {
      await putData(`/products/${updatedProduct.id}`, updatedProduct);
      await loadData(searchQuery, appliedModelFilter);
      closeEditModal();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'No se pudo actualizar el producto';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
  };

  return (
    <AdminPageShell
      title="Productos"
      actions={
        <>
          <Select
            size="sm"
            maxW="240px"
            bg="white"
            value={modelFilter}
            onChange={(e) => {
              setModelFilter(e.target.value);
            }}
            placeholder="Todos los modelos"
          >
            {models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.description}
              </option>
            ))}
          </Select>
          {canEdit && (
            <Button variant="adminPrimary" size="sm" minW="132px" onClick={openNewProductModal}>
              Nuevo producto
            </Button>
          )}
        </>
      }
    >
      <ProductsTable
        columnsData={
          canEdit
            ? columnsDataProducts
            : columnsDataProducts.filter((column) => column.accessor !== 'actions')
        }
        data={data}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
          pageSize={rowsPerPage}
          setPageSize={setRowsPerPage}
        handleUpdateProduct={handleUpdateProduct}
        sorting={sorting}
        onSortingChange={setSorting}
      />

      {canEdit && (
        <ProductsEditModal
          isOpen={modalState.isOpen}
          onClose={closeEditModal}
          product={modalState.product}
          isNew={modalState.isNew}
          onCreate={handleCreateProduct}
        />
      )}
    </AdminPageShell>
  );
}
